import { Dayjs } from 'dayjs';
import { isToday, isTomorrow } from '../../helpers/dateTimes';
import { CallbackTranslation } from '../../locale/localizationContext';
import {
  AttemptState,
  Problem,
  Delay,
  CalculatedOrderStatus,
} from '../../models';

export type GetDetailsHeading = {
  isInPo?: boolean;
  isLastAttempt: boolean | undefined;
  orderState: CalculatedOrderStatus;
  attempt: AttemptState | undefined;
  problem: Problem | undefined;
  arrivingDate?: Dayjs;
  t: CallbackTranslation;
};

export function getDetailsHeadingByOrderStateAndAttempt({
  isInPo,
  orderState,
  isLastAttempt,
  attempt,
  problem,
  arrivingDate,
  t,
}: GetDetailsHeading) {
  if (isInPo || isLastAttempt) return t('homeDetailsHeading.deliveryStock');

  if (orderState === CalculatedOrderStatus.GeocodingKo)
    return t('homeDetailsHeading.geocodingKo');

  if (orderState === CalculatedOrderStatus.ScheduledReception)
    return t('homeDetailsHeading.scheduledReception');

  if (orderState === CalculatedOrderStatus.Masked)
    return t('homeDetailsHeading.masked');

  if (orderState === CalculatedOrderStatus.Missed)
    return t('homeDetailsHeading.missed');

  if (orderState === CalculatedOrderStatus.Failed)
    return t('homeDetailsHeading.failed');

  if (orderState === CalculatedOrderStatus.StandBy)
    return t('homeDetailsHeading.standBy');

  if (orderState === CalculatedOrderStatus.TooSoon)
    return t('homeDetailsHeading.tooSoon');

  if (orderState === CalculatedOrderStatus.ProtectionMode)
    return t('homeDetailsHeading.protectionMode');

  if (orderState === CalculatedOrderStatus.Arriving)
    return t('homeDetailsHeading.arriving');

  if (orderState === CalculatedOrderStatus.InTransit)
    return t('homeDetailsHeading.deliveryInTransit');

  if (orderState === CalculatedOrderStatus.Fulfilled)
    return t('homeDetailsHeading.delivered');

  if (orderState === CalculatedOrderStatus.Scheduled) {
    if (isToday(arrivingDate!)) return t('homeDetailsHeading.scheduledToday');

    if (isTomorrow(arrivingDate!))
      return t('homeDetailsHeading.scheduledTomorrow');

    return t('homeDetailsHeading.scheduled');
  }

  const isSpecificMessage =
    (problem === Problem.F10 ||
      problem === Problem.F99 ||
      attempt === AttemptState.First ||
      attempt === AttemptState.Second) &&
    problem !== Problem.F06 &&
    problem !== Problem.F08 &&
    problem !== Problem.F11;

  if (isSpecificMessage) {
    switch (orderState) {
      case CalculatedOrderStatus.Blocked:
        return t('homeDetailsHeading.deliveryBlocked');
      case CalculatedOrderStatus.ToReschedule:
        return t('homeDetailsHeading.deliveryFailed');
      case CalculatedOrderStatus.Suspended:
        return t('homeDetailsHeading.deliverySuspended');
      default:
        return t('homeDetailsHeading.deliveryBlocked');
    }
  }
}

export type GetFirstDescriptionParams = {
  problem: Problem | undefined;
  orderState: CalculatedOrderStatus | undefined;
  delay: Delay | undefined;
  arrivingDate?: Dayjs;
  merchantName?: string;
  t: CallbackTranslation;
};

export function getFirstDescription({
  delay,
  orderState,
  arrivingDate,
  problem,
  t,
  merchantName,
}: GetFirstDescriptionParams) {
  if (orderState === CalculatedOrderStatus.Masked)
    return t('homeDetailsFirstDescription.masked');

  if (orderState === CalculatedOrderStatus.GeocodingKo)
    return t('homeDetailsFirstDescription.geocodingKo');

  if (orderState === CalculatedOrderStatus.ScheduledReception)
    return t('homeDetailsFirstDescription.scheduledReception');

  if (orderState === CalculatedOrderStatus.Missed)
    return t('homeDetailsFirstDescription.missed');

  if (orderState === CalculatedOrderStatus.Failed)
    return t('homeAdditionalNotes.failed');

  if (orderState === CalculatedOrderStatus.StandBy)
    return t('homeDetailsFirstDescription.standBy');

  if (orderState === CalculatedOrderStatus.TooSoon)
    return t('homeDetailsFirstDescription.tooSoon');

  if (orderState === CalculatedOrderStatus.ProtectionMode)
    return t('homeDetailsFirstDescription.protectionMode');

  if (orderState === CalculatedOrderStatus.Arriving)
    return t('homeDetailsFirstDescription.arriving', { merchantName });

  if (orderState === CalculatedOrderStatus.InTransit)
    return t('homeDetailsFirstDescription.deliveryInTransit');

  if (orderState === CalculatedOrderStatus.Suspended)
    return t('homeDetailsFirstDescription.suspended');

  if (orderState === CalculatedOrderStatus.Scheduled) {
    if (isToday(arrivingDate!))
      return t('homeDetailsFirstDescription.scheduledToday');

    if (isTomorrow(arrivingDate!))
      return t('homeDetailsFirstDescription.scheduledTomorrow');

    return t('homeDetailsFirstDescription.scheduled');
  }

  if (orderState === CalculatedOrderStatus.Fulfilled) {
    const fulfilledDescriptions = {
      [Delay.No]: t('homeDetailsFirstDescription.fulfilledNoDelay'),
      [Delay.Slight]: t('homeDetailsFirstDescription.fulfilledSlightDelay'),
      [Delay.Major]: t('homeDetailsFirstDescription.fulfilledMajorDelay'),
    };

    return fulfilledDescriptions[delay || Delay.No];
  }

  const descriptions = {
    [Problem.F01]: t('homeDetailsFirstDescription.recipientAbsent'),
    [Problem.F02]: t('homeDetailsFirstDescription.receptionClosed'),
    [Problem.F03]: t('homeDetailsFirstDescription.missingIntercomeName'),
    [Problem.F04]: t('homeDetailsFirstDescription.streetNumberNotFound'),
    [Problem.F05]: t('homeDetailsFirstDescription.refusedByTheRecipient'),
    [Problem.F06]: t('homeDetailsFirstDescription.marksNotPaid'),
    [Problem.F07]: t('homeDetailsFirstDescription.exceptionalCases'),
    [Problem.F08]: t('homeDetailsFirstDescription.unassignableReserve'),
    [Problem.F09]: t('homeDetailsFirstDescription.wrongAddress'),
    [Problem.F10]: t('homeDetailsFirstDescription.packageNotFound'),
    [Problem.F11]: t('homeDetailsFirstDescription.damagedPack'),
    [Problem.F99]: t('homeDetailsFirstDescription.declaredByTheDriver'),
  };
  return descriptions[problem!];
}

type StockInPoOptions = {
  poName?: string;
  hour?: string;
  date?: string;
};

export type GetSecondDescriptionParams = {
  isLastAttempt: boolean | undefined;
  orderState: CalculatedOrderStatus;
  delay: Delay | undefined;
  problem: Problem | undefined;
  attempt: AttemptState;
  t: CallbackTranslation;
  options: StockInPoOptions | undefined;
};

export function getSecondDescription({
  isLastAttempt,
  attempt,
  delay,
  options,
  orderState,
  problem,
  t,
}: GetSecondDescriptionParams) {
  if (options) return t('homeDetailsSecondDescription.inStockInPo', options);

  if (
    orderState === CalculatedOrderStatus.Scheduled ||
    orderState === CalculatedOrderStatus.ScheduledReception ||
    orderState === CalculatedOrderStatus.Masked ||
    orderState === CalculatedOrderStatus.ProtectionMode ||
    orderState === CalculatedOrderStatus.GeocodingKo ||
    orderState === CalculatedOrderStatus.StandBy ||
    orderState === CalculatedOrderStatus.Failed ||
    orderState === CalculatedOrderStatus.Missed ||
    orderState === CalculatedOrderStatus.TooSoon
  ) {
    return '';
  }

  if (orderState === CalculatedOrderStatus.Arriving)
    return t('homeDetailsSecondDescription.arriving');

  if (orderState === CalculatedOrderStatus.InTransit) {
    switch (delay) {
      case Delay.No:
        return t('homeDetailsSecondDescription.deliveryInTransitNoDelay');
      case Delay.Slight:
        return t('homeDetailsSecondDescription.deliveryInTransitSlightDelay');
      case Delay.Major:
        return t('homeDetailsSecondDescription.deliveryInTransitInDelay');
      default:
        return t('homeDetailsSecondDescription.deliveryInTransitNoDelay');
    }
  }

  if (problem === Problem.F10)
    return t('homeDetailsSecondDescription.packageNotFound');

  if (problem === Problem.F99)
    return t('homeDetailsSecondDescription.declaredByTheDriver');

  const isPackInStock =
    isLastAttempt ||
    attempt === AttemptState.Third ||
    attempt === AttemptState.Fourth ||
    problem === Problem.F08 ||
    problem === Problem.F11 ||
    problem === Problem.F05 ||
    problem === Problem.F06;

  if (isPackInStock) return t('homeDetailsSecondDescription.inStock');

  switch (problem) {
    case Problem.F01:
      return t('homeDetailsSecondDescription.recipientAbsent');
    case Problem.F02:
      return t('homeDetailsSecondDescription.receptionClosed');
    case Problem.F03:
      return t('homeDetailsSecondDescription.missingIntercomeName');
    case Problem.F04:
      return t('homeDetailsSecondDescription.streetNumberNotFound');
    case Problem.F07:
      return t('homeDetailsSecondDescription.exceptionalCases');
    case Problem.F09:
      return t('homeDetailsSecondDescription.wrongAddress');
  }

  return '';
}
